import React, { memo, useCallback, useMemo, useState } from "react";

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  IconButton,
  Spacer,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";

import { useQuery } from "@apollo/client";
import {
  MessageThreadsQuery,
  MessageThreadsQueryVariables,
  UserRoleType,
} from "gql/graphql";
import {
  HashIcon,
  OutOfOfficeIcon,
  PeopleIcon,
  PeopleOutlineIcon,
  ReadOnlyIcon,
  RemovePersonIcon,
  UserMailIcon,
} from "modules/icons";
import MESSAGE_THREADS_QUERY from "modules/messaging/_networking/message-threads";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useTags } from "modules/providers/tags-provider";
import { useUsers } from "modules/providers/users-provider";
import MessageThreadMeRemovedHeader from "modules/messaging/_shared/message-thread-me-removed-header";
import UserMention from "modules/_shared/mentions/user-mention";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import UserAvatar from "modules/_shared/user-avatar";
import moment from "moment";
import colors from "app/colors";
import { MESSAGE_CALENDAR_FORMATS } from "modules/utils/Constants";
import {
  getMessageThreadTypeName,
  getUserMention,
  isMessageThreadReadOnly,
  isUserOutOfOffice,
  removedUsersForGroup,
  sortUsersByCallout,
} from "modules/utils/Functions";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import DistributionGroupActionablePill from "../messaging/_shared/distribution-group-actionable-pill";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import MessageThreadProfileDrawerUser from "./components/message-thread-profile-drawer-user";
import MessageThreadProfileDrawerModeratorUser from "./components/message-thread-profile-drawer-moderator-user";
import TagActionablePill from "modules/messaging/message-thread-profile/tag-actionable-pill";
import MessageThreadReadOnlyHeader from "modules/messaging/message-thread/message-thread-read-only-header";
import MessageThreadLinkedItems from "modules/messaging/message-thread/message-thread-linked-items";
import HappycoWorkOrderMessageThreadDrawerDetails from "modules/external/happyco/happyco-work-order-message-thread-drawer-details";
import VideoChatButton from "modules/_shared/buttons/video-chat-button";

interface IProps {
  messageThreadId: string;
}

const MessageThreadProfileDrawer = ({ messageThreadId }: IProps) => {
  const { getTagsById } = useTags();
  const { getUsersById, usersById } = useUsers();
  const { getDistributionGroupsById } = useDistributionGroups();
  const { openModal } = useOverlay();
  const [isShowingRemovedUsers, setIsShowingRemovedUsers] = useState(false);

  const { data, error } = useQuery<
    MessageThreadsQuery,
    MessageThreadsQueryVariables
  >(MESSAGE_THREADS_QUERY, {
    variables: {
      id: messageThreadId,
    },
  });

  const messageThread = useMemo(() => data?.messageThreads[0], [data]);

  const { getCanRemoveUser } = useRemovedFromThread(messageThread);

  const users = useMemo(() => {
    if (!messageThread) return [];

    return getUsersById(messageThread.accessUserIdList)
      .sort(sortUsersByCallout)
      .filter((user) => user?.active && user?.roleType !== UserRoleType.Email)
      .filter(
        (user) => !messageThread.createMessageUserIdList.includes(user.id),
      );
  }, [messageThread, getUsersById]);

  const _isMessageThreadReadOnly = useMemo(() => {
    if (!messageThread) return false;
    return isMessageThreadReadOnly(messageThread);
  }, [messageThread]);

  const moderatorUsers = useMemo(() => {
    if (!messageThread) return [];

    return getUsersById(messageThread.accessUserIdList)
      .sort(sortUsersByCallout)
      .filter((user) => user?.active && user?.roleType !== UserRoleType.Email)
      .filter((user) =>
        messageThread.createMessageUserIdList.includes(user.id),
      );
  }, [messageThread, getUsersById]);

  const emailUsers = useMemo(() => {
    if (!messageThread) return [];

    return getUsersById(messageThread.accessUserIdList)
      .sort(sortUsersByCallout)
      .filter((user) => user?.active && user?.roleType === UserRoleType.Email);
  }, [messageThread, getUsersById]);

  const removedUserList = useMemo(
    () => messageThread?.removedUserList ?? [],
    [messageThread],
  );

  const tags = useMemo(() => {
    if (!messageThread) return [];
    return getTagsById(messageThread.tagIdList);
  }, [messageThread, getTagsById]);

  const distributionGroups = useMemo(() => {
    if (!messageThread) return [];
    return getDistributionGroupsById(messageThread.distributionGroupIdList);
  }, [messageThread, getDistributionGroupsById]);

  const hasModifiedGroup = useMemo(
    () =>
      distributionGroups.some(
        (distributionGroup) =>
          removedUsersForGroup(distributionGroup, removedUserList).length,
      ),
    [distributionGroups, removedUserList],
  );

  const outOfOfficeUserCount = useMemo(
    () => users.filter((user) => isUserOutOfOffice(user)).length,
    [users],
  );

  const onRemoveUserClicked = useCallback(
    (userId: string) => () => {
      openModal({
        type: MODAL_TYPE.CONFIRM_REMOVE_USERS_GROUPS,
        id: messageThreadId,
        ids: [userId],
      });
    },
    [messageThreadId, openModal],
  );

  const onShowDeactivatedUsersClicked = useCallback(() => {
    setIsShowingRemovedUsers((previous) => !previous);
  }, []);

  if (error) {
    return <Text>{error.message}</Text>;
  }

  if (!messageThread) return null;

  const threadName = getMessageThreadTypeName(messageThread);

  return (
    <DrawerContent>
      <DrawerCloseButton right={0} left={1} />
      <DrawerHeader>
        <Center fontWeight="bold">{`${threadName} Details`}</Center>
      </DrawerHeader>

      <DrawerBody>
        <VStack spacing={6}>
          <MessageThreadMeRemovedHeader messageThread={messageThread} />
          <MessageThreadReadOnlyHeader messageThread={messageThread} />
          <MessageThreadLinkedItems messageThread={messageThread} />
          <HappycoWorkOrderMessageThreadDrawerDetails
            messageThread={messageThread}
          />
          {distributionGroups.length > 0 && (
            <VStack spacing={0} alignItems="flex-start" w="full">
              <HStack spacing={2}>
                <PeopleOutlineIcon boxSize={6} />
                <Text fontWeight={700} fontSize={18}>
                  {`Groups (${distributionGroups.length})`}
                </Text>
              </HStack>
              <Divider borderColor="black" pt={2} />
              {hasModifiedGroup && (
                <Text color={colors.grayMain} fontSize={12} pt={2}>
                  One or more groups have modified member lists
                </Text>
              )}
              <Wrap pt={2}>
                {distributionGroups.map((distributionGroup) => (
                  <WrapItem key={distributionGroup.id}>
                    <DistributionGroupActionablePill
                      distributionGroupId={distributionGroup.id}
                      messageThread={messageThread}
                    />
                  </WrapItem>
                ))}
              </Wrap>
            </VStack>
          )}
          {tags.length > 0 && (
            <VStack spacing={0} alignItems="flex-start" w="full">
              <HStack spacing={2}>
                <Center boxSize={6}>
                  <HashIcon boxSize={6} />
                </Center>
                <Text fontWeight={700} fontSize={18}>
                  {`Tags (${tags.length})`}
                </Text>
              </HStack>
              <Divider borderColor="black" pt={2} />
              <Wrap pt={2}>
                {tags.map((tag) => (
                  <WrapItem key={tag.id}>
                    <TagActionablePill
                      messageThread={messageThread}
                      tag={tag}
                    />
                  </WrapItem>
                ))}
              </Wrap>
            </VStack>
          )}
          <VStack spacing={0} alignItems="flex-start" w="full">
            <HStack justify="space-between" w="full">
              <HStack spacing={2}>
                <PeopleIcon boxSize={6} />
                <Text fontWeight={700} fontSize={18}>
                  {`People (${users.length})`}
                </Text>
              </HStack>
              <VideoChatButton messageThread={messageThread} />
            </HStack>
            <Divider borderColor="black" pt={2} />
            {outOfOfficeUserCount > 0 && (
              <HStack
                bg={colors.yellow20}
                w="full"
                p={1}
                borderRadius={4}
                mt={1}
              >
                <OutOfOfficeIcon boxSize={4} fill={colors.yellowDark} />
                <Text fontWeight={500} color={colors.gray50} fontSize={12}>
                  {outOfOfficeUserCount === 1
                    ? "1 person is away"
                    : `${outOfOfficeUserCount} people are away`}
                </Text>
              </HStack>
            )}
            {_isMessageThreadReadOnly ? (
              <VStack spacing={3} w="full" alignItems="flex-start">
                <HStack spacing={1}>
                  <ReadOnlyIcon boxSize={4} fill={colors.grayMain} />
                  <Text color={colors.grayMain} fontSize={14} fontWeight={400}>
                    {`People who can post messages to read-only ${threadName}`}
                  </Text>
                </HStack>
                {moderatorUsers.map((user) => (
                  <MessageThreadProfileDrawerModeratorUser
                    messageThread={messageThread}
                    user={user}
                    key={user.id}
                  />
                ))}
                <Divider borderColor={colors.gray30} />
              </VStack>
            ) : (
              <Text
                color={colors.grayMain}
                fontSize={14}
                fontWeight={400}
                pt={2}
              >
                {`People who can view and comment on this ${threadName}`}
              </Text>
            )}
            {messageThread.isLocked && (
              <Text color={colors.grayMain} fontSize={12} pt={2}>
                {`Note that people cannot be added or removed from a ${threadName} while
                it is set to Private.`}
              </Text>
            )}
            <VStack
              pt={2}
              alignItems="flex-start"
              w="full"
              divider={<Divider borderColor={colors.grayPrimary} />}
            >
              {users.map((user) => (
                <MessageThreadProfileDrawerUser
                  messageThread={messageThread}
                  user={user}
                  key={user.id}
                />
              ))}
            </VStack>
          </VStack>
          {emailUsers.length > 0 && (
            <VStack spacing={0} alignItems="flex-start" w="full">
              <HStack spacing={2}>
                <UserMailIcon boxSize={6} />
                <Text fontWeight={700} fontSize={18}>
                  {`Outside Organization (${emailUsers.length})`}
                </Text>
              </HStack>
              <Divider borderColor="black" pt={2} />
              <Text color={colors.grayMain} fontSize={12} pt={2}>
                {`People who can view and comment on this ${threadName} via email.`}
              </Text>
              <VStack pt={2} alignItems="flex-start" w="full">
                {emailUsers.map((user) => (
                  <React.Fragment key={user.id}>
                    <HStack w="full">
                      <UserAvatar userId={user.id} size="sm" />
                      <UserMention id={user.id} />
                      <Spacer />
                      {getCanRemoveUser(user.id) && (
                        <SyncoTooltip label="Remove from conversation">
                          <IconButton
                            icon={<RemovePersonIcon fill={colors.blue} />}
                            aria-label="remove user"
                            variant="ghost"
                            onClick={onRemoveUserClicked(user.id)}
                          />
                        </SyncoTooltip>
                      )}
                    </HStack>
                    <Divider
                      borderColor={
                        user.id === messageThread.createdUserId
                          ? colors.grayPrimary
                          : undefined
                      }
                    />
                  </React.Fragment>
                ))}
              </VStack>
            </VStack>
          )}
          {messageThread.removedUserList.length > 0 && (
            <Accordion
              w="full"
              allowToggle
              onChange={onShowDeactivatedUsersClicked}
            >
              <AccordionItem border={0}>
                <VStack spacing={0} alignItems="flex-start" pt={4}>
                  <AccordionButton p={0}>
                    <Text color={colors.blue} fontSize={12}>
                      {isShowingRemovedUsers ? "- Hide" : "+ Show"}{" "}
                      Deactivated/Removed Users (
                      {messageThread.removedUserList.length})
                    </Text>
                  </AccordionButton>
                  <Box w="full">
                    <AccordionPanel p={0}>
                      <VStack pt={2} alignItems="flex-start">
                        {messageThread.removedUserList.map((removedUser) => {
                          const user = usersById[removedUser.userId];
                          if (!user) return null;
                          return (
                            <React.Fragment key={user.id}>
                              <HStack w="full">
                                <Box opacity={0.6}>
                                  <UserAvatar userId={user.id} size="sm" />
                                </Box>
                                <Text color={colors.grayMain}>
                                  {getUserMention(user)}
                                </Text>
                                <Spacer />
                                {removedUser.createTime && (
                                  <Text fontSize={12} color={colors.grayMain}>
                                    Removed{" "}
                                    {moment(removedUser.createTime).calendar(
                                      MESSAGE_CALENDAR_FORMATS,
                                    )}
                                  </Text>
                                )}
                              </HStack>
                              <Divider
                                borderColor={
                                  user.id === messageThread.createdUserId
                                    ? colors.grayPrimary
                                    : undefined
                                }
                              />
                            </React.Fragment>
                          );
                        })}
                      </VStack>
                    </AccordionPanel>
                  </Box>
                </VStack>
              </AccordionItem>
            </Accordion>
          )}
        </VStack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(MessageThreadProfileDrawer);
